import {
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Tooltip,
} from '@dixa/lib-shared-habla';

import { TwitterAccount, twitterApi } from 'apis/twitter';
import { appActions } from 'app/app.actions';
import { MessagingQueueDetails } from 'app/views/layout/views/settings/views/messaging/components/queueDetails/messagingQueueDetails';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { TwitterModal } from './components/twitterModal/twitterModal';

export const Twitter = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { accounts, isFetching } = twitterApi.useReadAccountsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      accounts: data?.data.accounts || [],
    }),
  });

  const { settings, isSuccess: isSuccessSettings } =
    twitterApi.useReadSettingsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        settings: data?.data ?? { max_connected_accounts: 1 },
      }),
    });

  const [deleteAccount] = twitterApi.useDeleteAccountMutation();

  const handleRemove = async (account: TwitterAccount) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Remove Twitter account? @${account.screen_name}`,
            description: `Are you sure you want to remove your Twitter account "${account.alias_name}" from Dixa? Please note that this action cannot be undone.`,
            confirm: {
              label: 'Remove',
              async: true,
              asyncLabel: 'Removing...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        confirm(async () => {
          await deleteAccount({
            accountId: account.account_id,
          }).unwrap();

          notify(
            {
              title: 'Twitter account successfully removed.',
              time: 3000,
            },
            NotificationType.Success,
          );
        });
      } catch {
        notify(
          {
            title: 'An error occurred while removing the Twitter account.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const limitReached =
    isSuccessSettings && accounts.length >= settings.max_connected_accounts;

  return (
    <Page.Section
      title="Twitter"
      controls={
        <Tooltip
          disabled={!limitReached}
          head={'Twitter accounts limit reached'}
          content={`You already reached your limit of
            ${settings.max_connected_accounts} connected Twitter accounts. Get in touch with your account manager to increase the limit.`}
        >
          <TwitterModal
            buttonComponent={
              <Button variant="secondary" icon="plus" disabled={limitReached}>
                Twitter account
              </Button>
            }
          />
        </Tooltip>
      }
    >
      <DataTable
        data={accounts}
        columns={[
          {
            label: 'ID',
            selector: (account) => account.account_id,
            style: {
              width: '30%',
            },
          },
          {
            label: 'Name',
            selector: (account) => account.alias_name,
            style: {
              width: '30%',
            },
          },
          {
            label: 'Queue',
            selector: (account) => (
              <MessagingQueueDetails queueId={account.queue_id} />
            ),
            style: {
              width: '30%',
            },
          },
          {
            label: '',
            selector: (account) => (
              <FlexBox justifyContent="flex-end">
                <TwitterModal
                  account={account}
                  buttonComponent={<IconButton variant="tertiary" icon="pen" />}
                />

                <IconButton
                  variant="tertiary"
                  icon="trash"
                  onClick={() => handleRemove(account)}
                />
              </FlexBox>
            ),
            style: {
              width: 0,
            },
          },
        ]}
        tableStatusProps={{
          loading: isFetching,
          ...(!isFetching &&
            !accounts.length && {
              emptyState: {
                icon: 'twitter',
                title: 'Get started with Twitter',
                description:
                  'Click the "+ Twitter account" button above and we\'ll help you through the setup process of the Twitter channel.',
              },
            }),
        }}
      />
    </Page.Section>
  );
};
